import {
  matchesStory,
  buildWallStoryLookup,
  wallMatchesStory,
} from '@shared-lib/geometry/storyMembership';

// Stacked (multi-story) merges stamp `.story` on every room and structure
// element (see scan-registration/run-stacked.js placeStory). Single-story
// structures carry no `.story`, so an empty/one-entry result means "flat".
export const collectStoryNumbers = (...arrays) => {
  const stories = new Set();
  for (const arr of arrays) {
    for (const el of arr || []) {
      if (Number.isFinite(el?.story)) stories.add(el.story);
    }
  }
  return [...stories].sort((a, b) => a - b);
};

// `floors` is in the list even though `placeStory` never stamps it: RoomPlan
// and the alignment pass DO give a multistory capture's floor surfaces a
// finite `.story`, and without filtering, every story's floor polygon drew on
// every story — most visibly an upper-storey loft plate painted over the
// ground floor. Floors that carry no `.story` are still kept everywhere by the
// `keep` predicate below, so single-story captures are unaffected.
const STORY_FILTER_KEYS = [
  'walls',
  'doors',
  'windows',
  'openings',
  'objects',
  'floors',
];

// THE story-match predicate, and which storeys a structure wall belongs to:
// one rule with the inspection report's plans, in shared-lib.
export { matchesStory, buildWallStoryLookup, wallMatchesStory };

// An exterior wall is the outside face of one structure wall
// (`alignment.structureWallId`) and belongs wherever that wall does; its own
// stamp is the fallback when the wall is unknown to the lookup.
export const exteriorWallMatchesStory = (extWall, story, lookup) => {
  if (story == null) return true;
  // A facade merged across storeys (Step 17e) stamps every storey it spans
  // and every structure wall it stands on; a plain exterior wall has one of
  // each. It belongs on a floor if any parent does, or if it says so itself.
  if (Array.isArray(extWall?.stories) && extWall.stories.includes(story))
    return true;
  const parentIds = Array.isArray(extWall?.alignment?.structureWallIds)
    ? extWall.alignment.structureWallIds
    : [extWall?.alignment?.structureWallId];
  let sawLookup = false;
  for (const id of parentIds) {
    const stories = lookup?.get(id);
    if (!stories) continue;
    sawLookup = true;
    if (stories.has(story)) return true;
  }
  return sawLookup ? false : matchesStory(extWall, story);
};

// Which story should be in scope once the user picks `room`? Returns the story
// to switch to, or `undefined` for "leave the scope alone".
//
// Room isolation and the floor filter COMPOSE rather than override (the 3D
// scene's `dataToRender` intersects them; the plan and minimap run this
// module's `filterDisplayDataByStory` over an already room-scoped view), so
// isolating a room from another floor while a floor is in scope leaves an
// empty intersection and nothing on screen to explain why. The floor->room
// direction is already guarded — picking a floor drops the room isolation.
// This is the room->floor direction: picking a room follows the view to that
// room's own floor.
//
// `undefined` is the "no change" sentinel because `null` is a MEANINGFUL value
// here ("all floors"), exactly as it is for the controlled-story props — so a
// nullish return could not tell "stay put" apart from "unscope to all floors".
// Story-less rooms are visible on every floor (`matchesStory`), so following
// one would strand the floor the user was actually looking at.
//
// Following applies FROM "all floors" too, not just between floors. A room
// sits on exactly one storey, so once one is picked the other floors' ghosted
// outlines are no longer context — they are clutter drawn over the thing you
// just asked to look at. (This case returned `undefined` until 2026-08-23,
// on the narrower reading that all-floors has nothing to reconcile: true of
// the empty-intersection hazard, but it left the map showing three floors
// when the user had chosen a room on one.)
// `storyOptions` is REQUIRED to be meaningful: a single-story structure has no
// floor to follow to, and following anyway is actively destructive. Picking a
// room there set the story filter to 0, which the stale-story guard in
// `FloorplanView` then reset to null — and that reset carries no `keepRoom`,
// so `handleStoryChange` ran `exitToStructure()` and dropped the very room the
// user had just clicked. Symptom: single-story plans stopped isolating a
// selected room at all.
export const storyToFollowRoom = (selectedStory, room, storyOptions = []) => {
  if (storyOptions.length <= 1) return undefined;
  if (!Number.isFinite(room?.story)) return undefined;
  return room.story === selectedStory ? undefined : room.story;
};

// The structure walls in scope for a given floor.
//
// Structure walls are the ONLY directly selectable walls (`detectClickedElement`
// — room walls are surface veneers), and the floorplan canvas takes them as
// their own prop rather than inside its display data, so they never passed
// through `filterDisplayDataByStory`. Stacked stories share X/Z (`placeStory`
// lifts Y only), so an unscoped list leaves every other floor's walls clickable
// and snappable at their own footprint while the picked floor is the only one
// drawn. The floor-filter twin of `structureWallsForRoomWalls` (the
// room-isolation rule) — together: a wall that isn't drawn can't be clicked.
//
// Pass `rooms` and a double-height wall is kept on every floor a room sits
// on it from (`buildWallStoryLookup`); without them a wall is scoped by its
// own stamp alone.
//
// Identity-preserving when there is nothing to filter, so memoised callers
// (the canvas redraws on this array's identity) keep their reference.
export const filterWallsByStory = (walls, story, rooms) => {
  if (story == null || !Array.isArray(walls)) return walls;
  const lookup = rooms ? buildWallStoryLookup(rooms, walls) : null;
  return walls.filter((wall) => wallMatchesStory(wall, story, lookup));
};

// Opening arrays are scoped through their HOST WALL, not their own stamp —
// parity with the 3D scene's openingsFollowWalls rule. An opening's own
// `.story` is not trustworthy: the 2D editor hard-coded `story: 0` on
// user-added openings until 2026-08-05, and some scans mis-stamp them
// (openings a story off their own wall). One deliberate divergence from the
// 3D rule: when the parent wall is unresolvable (legacy dangling FKs), fall
// back to the opening's own stamp instead of dropping it — a plan glyph is
// informative, a floating 3D door panel is not.
const OPENING_KEYS = new Set(['doors', 'windows', 'openings']);

// Keep elements whose `.story` matches the selection. Elements with no finite
// `.story` are left visible in every story rather than silently dropped.
export const filterDisplayDataByStory = (displayData, story) => {
  if (story == null || !displayData) return displayData;
  const keep = (el) => matchesStory(el, story);
  const walls = displayData.walls || [];
  const exteriorWalls = displayData.exterior?.walls || [];
  // Double-height walls are on every floor a room sits on them from.
  const lookup = buildWallStoryLookup(displayData.rooms, walls);
  const keepWall = (w) => wallMatchesStory(w, story, lookup);
  const keepExteriorWall = (w) => exteriorWallMatchesStory(w, story, lookup);
  const wallById = new Map([...walls, ...exteriorWalls].map((w) => [w.id, w]));
  const keepOpening = (o) => {
    const wall = wallById.get(o?.parentWallIds?.[0]);
    if (!wall) return matchesStory(o, story);
    // On a wall that belongs to several floors the host can't say which one
    // the opening is on — a stairwell door is at ground level, not repeated
    // upstairs — so there its own stamp decides when it has one.
    const wallStories = lookup.get(wall.id);
    if (wallStories?.size > 1 && Number.isFinite(o?.story)) {
      return matchesStory(o, story);
    }
    return wallMatchesStory(wall, story, lookup);
  };
  const filtered = { ...displayData };
  if (Array.isArray(displayData.rooms)) {
    filtered.rooms = displayData.rooms.filter(keep);
  }
  for (const key of STORY_FILTER_KEYS) {
    if (Array.isArray(displayData[key])) {
      filtered[key] = displayData[key].filter(
        OPENING_KEYS.has(key) ? keepOpening : key === 'walls' ? keepWall : keep
      );
    }
  }
  if (Array.isArray(displayData.exterior?.walls)) {
    filtered.exterior = {
      ...displayData.exterior,
      walls: displayData.exterior.walls.filter(keepExteriorWall),
    };
  }
  // The demolished bucket's walls are structure walls a merge took out of
  // `walls`, so the filter above never sees them; scope them by their own
  // stamp, as the 3D scene's story filter does. Without this a ghost from an
  // upstairs merge is drawn across the floor below. No room sits on a
  // demolished wall any more, so the shared-wall lookup has nothing to add.
  if (Array.isArray(displayData.demolished?.walls)) {
    filtered.demolished = {
      ...displayData.demolished,
      walls: displayData.demolished.walls.filter(keep),
    };
  }
  return filtered;
};

// ---------------------------------------------------------------------------
// All-floors ghosting
//
// When a multi-story structure is shown with every floor at once, the stories
// sit at the same X/Z (placeStory lifts Y only), so the plan views would paint
// every floor on top of each other. The lowest story is drawn solid and each
// story above it is ghosted (tinted layer composited at this alpha — see
// utils/ghostLayer.js), stepping lighter the higher it is, so the alpha
// encodes the height order. Index = stories above the lowest.
// ---------------------------------------------------------------------------
export const GHOST_ALPHA_STEPS = [1, 0.5, 0.3];

// The storeys a plan view is STACKING, which is what the ghost alpha encodes.
//
// Ghosting exists only to separate floors drawn on top of each other, so once
// the user picks a floor there is nothing to separate and everything on screen
// is solid — say that outright rather than inferring it from what survived the
// story filter. Inferring it was wrong: `filterDisplayDataByStory` keeps a
// double-height wall on every floor a room sits on it from, but that wall's
// own `.story` is the storey it was stamped on, so one stairwell wall put a
// second number in the census and greyed out a whole upper floor that the user
// had asked to see alone (found 2026-09-02 on 7 of 26 multi-storey dev scans).
//
// `story == null` is the all-floors view, and only there does the census come
// from the data. Empty and single-entry results both mean "draw solid"
// (`storyGhostAlpha`), so the picked-floor case needs no special casing
// downstream.
export const ghostStoriesFor = (displayData, story) =>
  story == null
    ? collectStoryNumbers(displayData?.rooms, displayData?.walls)
    : [story];

export const lowestStory = (stories) => (stories.length ? stories[0] : null);

// Rooms of the lowest present story (unstamped rooms are kept — they are the
// single-story case and belong everywhere). Identity when there is nothing to
// separate, so memoised callers keep their reference.
export const lowestStoryRooms = (rooms) => {
  const stories = collectStoryNumbers(rooms);
  if (stories.length <= 1) return rooms;
  const lowest = lowestStory(stories);
  return (rooms || []).filter((room) => matchesStory(room, lowest));
};

// Room walls are commonly UNSTAMPED while their room is (see MinimapNav's
// rederiveRoomOwned note), so a wall's story has to be resolved through its
// room. Two routes, because the two plan renderers shape walls differently:
// the floor plan flattens with `_roomId`; the minimap's `data.walls` keep the
// room-side wall ids.
export const buildStoryLookup = (rooms, structureWalls = []) => {
  const byRoomId = new Map();
  const byWallId = new Map();
  for (const wall of structureWalls || []) {
    if (wall?.id && Number.isFinite(wall.story))
      byWallId.set(wall.id, wall.story);
  }
  for (const room of rooms || []) {
    if (!Number.isFinite(room?.story)) continue;
    if (room.id) byRoomId.set(room.id, room.story);
    for (const wall of room.walls || []) {
      if (wall?.id) byWallId.set(wall.id, room.story);
    }
  }
  return { byRoomId, byWallId };
};

export const resolveElementStory = (el, lookup) => {
  // Openings FIRST, through the host wall: their own stamp is untrustworthy
  // (the 2D editor hard-coded `story: 0` on user-added openings until
  // 2026-08-05, and some scans mis-stamp them), and `filterDisplayDataByStory`
  // draws them on their wall's story regardless. Only openings carry
  // `parentWallIds`, so nothing else takes this branch — and resolving one to
  // its own bad stamp put it in a different ghost bucket from the wall it is
  // carved into, and told the plan to keep its properties panel open on a
  // floor that no longer draws it.
  const parentId = el?.parentWallIds?.[0];
  const viaParent = parentId != null ? lookup.byWallId.get(parentId) : null;
  if (Number.isFinite(viaParent)) return viaParent;
  if (Number.isFinite(el?.story)) return el.story;
  const viaRoom = el?._roomId != null ? lookup.byRoomId.get(el._roomId) : null;
  if (Number.isFinite(viaRoom)) return viaRoom;
  const viaWall = el?.id != null ? lookup.byWallId.get(el.id) : null;
  return Number.isFinite(viaWall) ? viaWall : null;
};

export const storyGhostAlpha = (story, stories) => {
  if (stories.length <= 1 || !Number.isFinite(story)) return 1;
  const stepsAbove = stories.indexOf(story);
  if (stepsAbove <= 0) return 1;
  return GHOST_ALPHA_STEPS[Math.min(stepsAbove, GHOST_ALPHA_STEPS.length - 1)];
};

// Group elements by their ghost alpha, most-ghosted first, so a caller can
// draw each bucket under `ctx.globalAlpha` and the solid lowest story lands on
// top of the upper floors.
export const bucketByGhostAlpha = (elements, stories, lookup) => {
  const byAlpha = new Map();
  for (const el of elements || []) {
    const alpha = storyGhostAlpha(resolveElementStory(el, lookup), stories);
    if (!byAlpha.has(alpha)) byAlpha.set(alpha, []);
    byAlpha.get(alpha).push(el);
  }
  return [...byAlpha.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([alpha, els]) => ({ alpha, elements: els }));
};

// Floors are labelled from the story index — `Floor 1` is story 0 — so there
// is nothing to store or name. The floor picker (FloorSelect) and the
// estimate's room list (utils/estimate/roomScopeList) both label with this.
export const floorLabel = (story) =>
  story === null ? 'All floors' : `Floor ${story + 1}`;
